import React, { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import FormField from '../../../Components/FormField'
import CurrentImagePreview from '../../../Components/CurrentImagePreview'
import { SittingTimesEditor } from './components/SittingTimesEditor'
import { UpsellEditor } from './components/UpsellEditor'
import { ScheduleEditor } from './components/ScheduleEditor'
import { validateSittingTimes } from './utils/sittingTimeValidation'

export default function Index({ account, accounts, navigation, settings, days_of_week, is_account_admin }) {
  const [form, setForm] = useState({
    description: settings.description || '',
    phone: settings.phone || '',
    contact_email: settings.contact_email || '',
    website: settings.website || '',
    social_x: settings.social_x || '',
    social_facebook: settings.social_facebook || '',
    social_instagram: settings.social_instagram || '',
    social_youtube: settings.social_youtube || '',
    kitchen_unlock_code: settings.kitchen_unlock_code || '',
    new_order_reminder_timeout: settings.new_order_reminder_timeout || 0,
    kitchen_notification_sound: settings.kitchen_notification_sound || 'notification',
    schedule: settings.schedule || {},
    kitchen_schedule: settings.kitchen_schedule || {},
    sitting_times: settings.sitting_times || {},
    offer_full_menu: settings.offer_full_menu ?? false,
    promo_title: settings.promo_title || '',
    promo_message: settings.promo_message || '',
    promo_action_text: settings.promo_action_text || '',
    promo_action_url: settings.promo_action_url || '',
    dress_code: settings.dress_code || '',
    parking_info: settings.parking_info || '',
    address_attributes: {
      id: settings.address?.id || '',
      line_one: settings.address?.line_one || '',
      line_two: settings.address?.line_two || '',
      city: settings.address?.city || '',
      county: settings.address?.county || '',
      postcode: settings.address?.postcode || ''
    }
  })
  const [qrLogo, setQrLogo] = useState(null)
  const [coverImage, setCoverImage] = useState(null)
  const [logo, setLogo] = useState(null)

  // Build upsell form state keyed by sitting from the existing upsells array
  const [upsellForm, setUpsellForm] = useState(() => {
    const initial = {}
    ;(settings.upsells || []).forEach(u => {
      initial[u.sitting] = { menu_item_id: u.menu_item_id, cta_title: u.cta_title }
    })
    return initial
  })

  const handleUpsellChange = (sitting, field, value) => {
    setUpsellForm(prev => ({
      ...prev,
      [sitting]: {
        ...prev[sitting],
        [field]: value,
        // Clear cta_title when menu item is removed
        ...(field === 'menu_item_id' && !value ? { cta_title: '' } : {})
      }
    }))
  }

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleAddressChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({
      ...prev,
      address_attributes: { ...prev.address_attributes, [name]: value }
    }))
  }

  const handleScheduleChange = (scheduleType, day, field, value) => {
    setForm(prev => ({
      ...prev,
      [scheduleType]: {
        ...prev[scheduleType],
        [day]: {
          ...prev[scheduleType]?.[day],
          [field]: value
        }
      }
    }))
  }

  const handleSittingTimeChange = (sitting, field, value) => {
    setForm(prev => ({
      ...prev,
      sitting_times: {
        ...prev.sitting_times,
        [sitting]: {
          ...prev.sitting_times?.[sitting],
          [field]: value
        }
      }
    }))
  }

  const sittingTimeErrors = useMemo(() => {
    return validateSittingTimes(form.sitting_times)
  }, [form.sitting_times])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (sittingTimeErrors.length > 0) {
      return
    }

    const payload = { account: form, upsells: upsellForm }
    const hasFiles = qrLogo || coverImage || logo

    if (hasFiles) {
      payload.account = { ...form }
      if (qrLogo) payload.account.qr_logo = qrLogo
      if (coverImage) payload.account.cover_image = coverImage
      if (logo) payload.account.logo = logo
      router.post('/manage/settings', { ...payload, _method: 'PATCH' }, { forceFormData: true })
    } else {
      router.patch('/manage/settings', payload)
    }
  }

  return (
    <Layout account={account} accounts={accounts} navigation={navigation} title="Settings">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Club Profile */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Club Profile</h3>
            <p className="mt-1 text-sm text-gray-500">Public profile shown on your club directory page</p>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6">
            <FormField
              label="Description"
              name="description"
              type="textarea"
              value={form.description}
              onChange={handleChange}
              placeholder="Tell golfers about your club..."
              rows={3}
              hint="A short description shown on your public club page."
            />
            <div>
              <CurrentImagePreview
                url={settings.logo_url}
                alt="Club logo"
                label="Current Logo"
                className="h-20 w-20 rounded-lg object-cover"
              />
              <div className="max-w-xs mt-4">
                <FormField
                  label={settings.logo_url ? "Replace Logo" : "Upload Logo"}
                  name="logo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(file) => setLogo(file)}
                  hint="PNG, JPEG, or WebP, max 2MB. Shown on the club directory and your club page."
                />
              </div>
            </div>
            <div>
              <CurrentImagePreview
                url={settings.cover_image_url}
                alt="Club cover image"
                label="Current Cover Image"
                className="h-32 w-full max-w-md rounded-lg object-cover"
              />
              <div className="max-w-xs mt-4">
                <FormField
                  label={settings.cover_image_url ? "Replace Cover Image" : "Upload Cover Image"}
                  name="cover_image"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(file) => setCoverImage(file)}
                  hint="PNG, JPEG, or WebP, max 5MB. Displayed as a background on your club page."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
            <p className="mt-1 text-sm text-gray-500">Contact details shown to customers</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Phone Number"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="0141 880 1234"
              />
              <FormField
                label="Contact Email"
                name="contact_email"
                type="email"
                value={form.contact_email}
                onChange={handleChange}
                placeholder="contact@example.com"
              />
              <FormField
                label="Website"
                name="website"
                type="url"
                value={form.website}
                onChange={handleChange}
                placeholder="https://www.yourclub.com"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Social Media</h3>
            <p className="mt-1 text-sm text-gray-500">Links shown on your public club page</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="X (Twitter)"
                name="social_x"
                type="url"
                value={form.social_x}
                onChange={handleChange}
                placeholder="https://x.com/yourclub"
              />
              <FormField
                label="Facebook"
                name="social_facebook"
                type="url"
                value={form.social_facebook}
                onChange={handleChange}
                placeholder="https://facebook.com/yourclub"
              />
              <FormField
                label="Instagram"
                name="social_instagram"
                type="url"
                value={form.social_instagram}
                onChange={handleChange}
                placeholder="https://instagram.com/yourclub"
              />
              <FormField
                label="YouTube"
                name="social_youtube"
                type="url"
                value={form.social_youtube}
                onChange={handleChange}
                placeholder="https://youtube.com/@yourclub"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Location</h3>
            <p className="mt-1 text-sm text-gray-500">Your club's address, shown on your public page with a map</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <FormField
                  label="Address Line 1"
                  name="line_one"
                  value={form.address_attributes.line_one}
                  onChange={handleAddressChange}
                  placeholder="e.g. 123 Fairway Drive"
                />
              </div>
              <div className="md:col-span-2">
                <FormField
                  label="Address Line 2"
                  name="line_two"
                  value={form.address_attributes.line_two}
                  onChange={handleAddressChange}
                  placeholder="Optional"
                />
              </div>
              <FormField
                label="City"
                name="city"
                value={form.address_attributes.city}
                onChange={handleAddressChange}
                placeholder="e.g. Glasgow"
              />
              <FormField
                label="County"
                name="county"
                value={form.address_attributes.county}
                onChange={handleAddressChange}
                placeholder="e.g. Lanarkshire"
              />
              <FormField
                label="Postcode"
                name="postcode"
                value={form.address_attributes.postcode}
                onChange={handleAddressChange}
                placeholder="e.g. G1 1AA"
              />
            </div>
          </div>
        </div>

        {/* Promo Message */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Promo Message</h3>
            <p className="mt-1 text-sm text-gray-500">Highlight a promotion or announcement on your club page</p>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6">
            <FormField
              label="Title"
              name="promo_title"
              value={form.promo_title}
              onChange={handleChange}
              placeholder="e.g. Summer Special"
              hint="Leave blank to hide the promo section."
            />
            <FormField
              label="Message"
              name="promo_message"
              type="textarea"
              value={form.promo_message}
              onChange={handleChange}
              placeholder="e.g. 20% off all food orders this weekend"
              rows={2}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Action Text"
                name="promo_action_text"
                value={form.promo_action_text}
                onChange={handleChange}
                placeholder="e.g. Learn More"
              />
              <FormField
                label="Action URL"
                name="promo_action_url"
                type="url"
                value={form.promo_action_url}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        {/* Club Details */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Club Details</h3>
            <p className="mt-1 text-sm text-gray-500">Useful information for visitors</p>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6">
            <FormField
              label="Dress Code"
              name="dress_code"
              type="textarea"
              value={form.dress_code}
              onChange={handleChange}
              placeholder="e.g. Smart casual. No denim or trainers on the course."
              rows={3}
            />
            <FormField
              label="Parking Information"
              name="parking_info"
              type="textarea"
              value={form.parking_info}
              onChange={handleChange}
              placeholder="e.g. Free parking available for 100 cars."
              rows={3}
            />
          </div>
        </div>

        {/* Kitchen Display Settings */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Kitchen Display</h3>
            <p className="mt-1 text-sm text-gray-500">Settings for the kitchen display screen</p>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6">
            <div className="max-w-xs">
              <FormField
                label="Kitchen Unlock Code"
                name="kitchen_unlock_code"
                type="text"
                value={form.kitchen_unlock_code}
                onChange={handleChange}
                placeholder="123456"
                maxLength={6}
                pattern="[0-9]{6}"
                inputMode="numeric"
              />
              <p className="mt-2 text-sm text-gray-500">
                6-digit code to unlock the kitchen screen after inactivity. Leave blank to allow unlock without a code.
              </p>
            </div>
            <div className="max-w-xs">
              <FormField
                label="New Order Reminder (seconds)"
                name="new_order_reminder_timeout"
                type="number"
                min={0}
                step={1}
                value={form.new_order_reminder_timeout}
                onChange={handleChange}
                placeholder="0"
                hint="Seconds between reminder sounds for unactioned orders. Set to 0 to disable."
              />
            </div>
            <div className="max-w-xs">
              <FormField
                label="Notification Sound"
                name="kitchen_notification_sound"
                type="select"
                value={form.kitchen_notification_sound}
                onChange={handleChange}
                options={['notification', 'horn', 'alarm', 'balloon']}
                hint="Sound played when new orders arrive in the kitchen display."
              />
            </div>
          </div>
        </div>

        {/* QR Code Logo */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">QR Code Logo</h3>
            <p className="mt-1 text-sm text-gray-500">Logo displayed in the centre of your QR codes</p>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-4">
            <CurrentImagePreview
              url={settings.qr_logo_url}
              alt="QR code logo"
              label="Current Logo"
              className="h-20 w-20 rounded-lg object-cover"
            />
            <div className="max-w-xs">
              <FormField
                label={settings.qr_logo_url ? "Replace Logo" : "Upload Logo"}
                name="qr_logo"
                type="file"
                accept="image/png,image/jpeg"
                onChange={(file) => setQrLogo(file)}
                hint="PNG or JPEG, max 2MB. Leave empty to use the default logo."
              />
            </div>
          </div>
        </div>

        {/* Payments - Admin only */}
        {is_account_admin && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Payments</h3>
              <p className="mt-1 text-sm text-gray-500">Manage your Stripe account and payment settings</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <a
                href="/manage/stripe_connect"
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
              >
                <CreditCardIcon className="h-4 w-4 mr-2" />
                Manage Payments
              </a>
            </div>
          </div>
        )}

        {/* General Opening Hours */}
        <ScheduleEditor
          title="General Opening Hours"
          scheduleType="schedule"
          schedule={form.schedule}
          daysOfWeek={days_of_week}
          onScheduleChange={handleScheduleChange}
        />

        {/* Kitchen Hours */}
        <ScheduleEditor
          title="Kitchen Hours"
          scheduleType="kitchen_schedule"
          schedule={form.kitchen_schedule}
          daysOfWeek={days_of_week}
          onScheduleChange={handleScheduleChange}
        />

        {/* Menu Sitting Times */}
        <SittingTimesEditor
          sittingTimes={form.sitting_times}
          onSittingTimeChange={handleSittingTimeChange}
          errors={sittingTimeErrors}
        />

        {/* Full Menu */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Full Menu</h3>
            <p className="mt-1 text-sm text-gray-500">Control how much of your menu guests can order at once</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <FormField
              label="Let guests order the full menu at any time"
              name="offer_full_menu"
              type="checkbox"
              value={form.offer_full_menu}
              onChange={handleChange}
              hint="Guests can browse and order your whole menu at any time of day, with a search box to find items fast. Sitting times still control which items are featured first — items with no sitting assigned appear in the full menu only. Leave off to serve only the current sitting's items."
            />
          </div>
        </div>

        {/* Checkout Upsells */}
        <UpsellEditor
          upsellForm={upsellForm}
          menuItems={settings.menu_items_for_upsell || []}
          onUpsellChange={handleUpsellChange}
        />

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={sittingTimeErrors.length > 0}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Save Settings
          </button>
        </div>
      </form>
    </Layout>
  )
}

function CreditCardIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  )
}
